import { create } from "zustand";
import * as SharedMessageTypes from "../../darvin-shared/schema/zod/agent/message-types.schema";
import { useProjectStore } from "../core_stores/useProjectStore";
import {
  ProjectChatAssistantMessage,
  ProjectChatElement,
} from "./chat_messages/chatModels";
import { useChatMessagesStore } from "./chat_messages/useChatMessagesStore";

interface PromptErrorHandlerActions {
  initialize: () => void;
  rollback: (newPrompt: string | null, newImage: File | null) => Promise<void>;
  discard: () => Promise<void>;
  dispose: () => void;
}

interface PromptErrorDetails {
  message: string;
  reason: SharedMessageTypes.PromptErrorReason;
}

interface PromptErrorHandlerState {
  canHandlePromptError: boolean;
  promptErrorDetails: PromptErrorDetails | null;

  actions: PromptErrorHandlerActions;
}

const usePromptErrorHandlerStore = create<PromptErrorHandlerState>((set) => {
  let unsubscribeChatElements: (() => void) | null = null;
  let pendingJobDoneSubscription: (() => void) | null = null;
  let prompt: string | null = null;
  let image: File | null = null;

  function getPromptErrorDetails(
    chatElements: ProjectChatElement[],
  ): PromptErrorDetails | null {
    if (chatElements.length === 0) {
      return null;
    }
    const lastMessage = chatElements[chatElements.length - 1];

    const isLastMsgPromptError =
      lastMessage instanceof ProjectChatAssistantMessage &&
      lastMessage.type === "error" &&
      (lastMessage.value as SharedMessageTypes.ErrorContent).type ===
        SharedMessageTypes.ErrorTypeEnum.enum.prompt;

    if (isLastMsgPromptError) {
      const hasCheckpoint = hasLastUserMsgCheckpointId();

      if (hasCheckpoint) {
        const errorContent =
          lastMessage.value as SharedMessageTypes.ErrorContent;
        // Type narrowing for prompt error
        if (errorContent.type === "prompt") {
          return {
            message: errorContent.message,
            reason: errorContent.reason,
          };
        }
      }
    }
    return null;
  }

  function hasLastUserMsgCheckpointId(): boolean {
    const lastUserMsg = useChatMessagesStore.getState().lastUserMessage;
    return lastUserMsg?.checkpointId !== undefined;
  }

  async function sendMessage() {
    await useProjectStore.getState().actions.sendMessage(prompt!, image);
    await useChatMessagesStore
      .getState()
      .actions.addTemporaryMessage(prompt!, image);
  }

  return {
    canHandlePromptError: false,
    promptErrorDetails: null,

    actions: {
      initialize: () => {
        unsubscribeChatElements = useChatMessagesStore.subscribe((state) => {
          const errorDetails = getPromptErrorDetails(
            state.chatElements.elements ?? [],
          );
          set({
            canHandlePromptError: errorDetails !== null,
            promptErrorDetails: errorDetails,
          });
        });
      },

      rollback: async (newPrompt: string | null, newImage: File | null) => {
        prompt = newPrompt;
        image = newImage;
        await useProjectStore.getState().actions.rollback();
        if (prompt) {
          await new Promise<void>((resolve, reject) => {
            // Clean up any existing pending subscription before creating a new one
            if (pendingJobDoneSubscription) {
              pendingJobDoneSubscription();
              pendingJobDoneSubscription = null;
            }

            pendingJobDoneSubscription = useProjectStore.subscribe(
              (state) => state.latestMsg,
              async (latestMsg) => {
                if (
                  latestMsg?.type ===
                  SharedMessageTypes.MessageTypeEnum.enum.jobDone
                ) {
                  if (pendingJobDoneSubscription) {
                    pendingJobDoneSubscription();
                    pendingJobDoneSubscription = null;
                  }
                  try {
                    await sendMessage();
                    resolve();
                  } catch (error) {
                    reject(error);
                  }
                }
              },
            );
          });
        }
        prompt = null;
        image = null;
      },

      discard: async () => {
        await useProjectStore.getState().actions.rollback();
        set({ canHandlePromptError: false, promptErrorDetails: null });
      },

      dispose: () => {
        if (unsubscribeChatElements) {
          unsubscribeChatElements();
          unsubscribeChatElements = null;
        }
        if (pendingJobDoneSubscription) {
          pendingJobDoneSubscription();
          pendingJobDoneSubscription = null;
        }
        set({ canHandlePromptError: false, promptErrorDetails: null });
        prompt = null;
        image = null;
      },
    },
  };
});

export const useCanHandlePromptError = () =>
  usePromptErrorHandlerStore((state) => state.canHandlePromptError);

export const usePromptErrorDetails = () =>
  usePromptErrorHandlerStore((state) => state.promptErrorDetails);

export const usePromptErrorHandlerActions = () =>
  usePromptErrorHandlerStore((state) => state.actions);
